/**
 * SSE 处理 Mixin
 * 负责管理 SSE 连接和处理各类 SSE 事件
 * 依赖: messageManager.js, typewriterEffect.js
 */
import { parseRelatedQuestions, removeRelatedQuestions } from "@/utils/parseRelatedQuestions.js";
import { setHistoryConversationId, getHistoryConversationId } from "@/utils/storage.js";

export default {
  data() {
    return {
      sseCloseConnection: null, // 保存 SSE 连接关闭函数
      conversation_id: getHistoryConversationId(),
    };
  },

  methods: {
    /**
     * 去除末尾未完整的 Markdown 图片语法
     * 场景示例：
     * - "这里是文字 !"
     * - "这里是文字 ![alt"
     * - "这里是文字 ![](http..."
     * 这些半截图片前缀都不应该进入打字机队列
     * @param {string} content
     * @returns {{safeContent: string, wasTruncated: boolean, truncatedAt: number}}
     */
    getSafeContentWithoutTrailingImage(content) {
      if (!content) return { safeContent: content || "", wasTruncated: false, truncatedAt: -1 };

      // 找到最后一个可能的图片起始符号 "!"
      const bangIndex = content.lastIndexOf("!");
      if (bangIndex === -1) {
        return { safeContent: content, wasTruncated: false, truncatedAt: -1 };
      }

      const tail = content.slice(bangIndex); // 从 "!" 开始到结尾

      // 检查是否是完整的 markdown 图片（![...](...)）
      const fullImagePattern = /^!\[[^\]]*]\([^)]*\)/;
      if (fullImagePattern.test(tail)) {
        return { safeContent: content, wasTruncated: false, truncatedAt: -1 };
      }

      // 检查是否可能是图片的前缀（!、![、![alt、![](、![](http... 等）
      // 如果末尾是 "!" 或 "![" 或 "![..." 但不完整，都应该截断
      const imageStartPattern = /^!/; // 只要以 "!" 开头就可能是图片前缀
      if (imageStartPattern.test(tail)) {
        // 这可能是未完整的图片前缀，先不显示
        return { 
          safeContent: content.slice(0, bangIndex), 
          wasTruncated: true, 
          truncatedAt: bangIndex 
        };
      }

      // 其他情况，返回完整内容
      return { safeContent: content, wasTruncated: false, truncatedAt: -1 };
    },

    /**
     * SSE 事件分发器
     * @param {object} eventData - SSE 事件数据
     */
    handleSSEEvent(eventData) {
      switch (eventData.event) {
        case "workflow_started":
          this.handleWorkflowStarted(eventData);
          break;

        case "node_started":
        case "node_finished":
          // 节点开始/结束事件，可以记录日志但不需要显示
          break;

        case "message":
          this.handleMessageEvent(eventData);
          break;

        case "message_end":
          break;

        case "workflow_finished":
          this.handleWorkflowFinished(eventData);
          break;

        default:
          // 如果未知事件包含答案数据，也尝试处理
          if (eventData.answer) {
            this.handleMessageEvent(eventData);
          }
      }
    },


    /**
     * 处理工作流开始事件
     * @param {object} data - 事件数据
     */
    handleWorkflowStarted(data) {
      this.currentMessageId = data.message_id;
      this.conversation_id = data.conversation_id;
      setHistoryConversationId(this.conversation_id);
      // 使用 messageManager 添加 AI 消息
      if (this.addAIMessage) {
        this.addAIMessage(data);
      }

      // 重置打字机状态
      if (this.resetTypewriter) {
        this.resetTypewriter();
      }
    },


    /**
     * 处理消息事件
     * @param {object} data - 事件数据
     */
    handleMessageEvent(data) {
      const answerText = data.answer;
      if (answerText == null) {
        return;
      }
      // 累积流式内容
      this.currentAnswer += answerText;

      // 查找对应的 AI 消息
      const aiMessage = this.findAIMessage(this.currentMessageId);

      if (!aiMessage) {
        console.warn(
          "未找到对应的 AI 消息，currentMessageId:",
          this.currentMessageId
        );
        return;
      }

      // 更新完整内容
      aiMessage.content = this.currentAnswer;
      
      // 检测"相关问题："标记，避免相关问题部分在打字机效果中显示
      const marker = '相关问题：';
      const markerIndex = this.currentAnswer.indexOf(marker);

      // 用于存储安全内容（去除未完整图片和相关问题标记后的内容）
      let safeResult = { safeContent: '', wasTruncated: false, truncatedAt: -1 };
      
      if (markerIndex !== -1) {
        // 发现完整标记，只处理标记之前的内容
        const cleanContent = this.currentAnswer.substring(0, markerIndex).trim();
        safeResult = this.getSafeContentWithoutTrailingImage(cleanContent);
      } else {
        // 未发现完整标记，检查末尾是否可能是标记的前缀
        // "相关问题：" 的前缀可能是 "相关问题"、"相关问"、"相关" 等
        let maxPrefixLength = 0;
        const markerLength = marker.length;
        const textLength = this.currentAnswer.length;
        
        // 检查末尾是否可能是标记的前缀（最多检查标记长度-1个字符）
        for (let i = Math.min(textLength, markerLength - 1); i >= 1; i--) {
          const suffix = this.currentAnswer.slice(-i);
          if (marker.startsWith(suffix)) {
            maxPrefixLength = i;
            break;
          }
        }
        
        if (maxPrefixLength > 0) {
          // 末尾可能是标记前缀，只处理前缀之前的内容
          const safeContent = this.currentAnswer.substring(0, textLength - maxPrefixLength);
          safeResult = this.getSafeContentWithoutTrailingImage(safeContent);
        } else {
          // 确认不是标记前缀，处理所有内容
          safeResult = this.getSafeContentWithoutTrailingImage(this.currentAnswer);
        }
      }

      // 初始化追踪变量
      if (!aiMessage.lastSafeContentLength) {
        aiMessage.lastSafeContentLength = 0;
      }
      if (aiMessage.lastTruncatedAt === undefined) {
        aiMessage.lastTruncatedAt = -1;
      }
      
      // 计算新增的内容
      let contentToAdd = '';
      let startPosition = aiMessage.lastSafeContentLength;
      
      // 如果上一次被截断了，但这次没有截断（或截断位置不同），说明之前被截断的内容现在完整了
      // 需要从上次的截断位置重新开始添加
      if (aiMessage.lastTruncatedAt !== -1 && 
          (!safeResult.wasTruncated || safeResult.truncatedAt !== aiMessage.lastTruncatedAt)) {
        startPosition = aiMessage.lastTruncatedAt;
      }
      
      if (safeResult.safeContent.length > startPosition) {
        contentToAdd = safeResult.safeContent.substring(startPosition);
        aiMessage.lastSafeContentLength = safeResult.safeContent.length;
      }
      
      // 更新截断状态
      aiMessage.lastTruncatedAt = safeResult.wasTruncated ? safeResult.truncatedAt : -1;

      // 添加内容到打字机队列
      if (contentToAdd) {
        if (this.addToTypewriterQueue) {
          this.addToTypewriterQueue(contentToAdd);
        } else {
          // 降级处理：如果没有 addToTypewriterQueue 方法，直接添加文本 Token
          const tokens = this.parseTextToTokens ? this.parseTextToTokens(contentToAdd) : [{ type: 'text', content: contentToAdd }];
          this.typewriterQueue.push(...tokens);
        }
      }

      // 启动打字机效果
      if (this.startTypewriter) {
        this.startTypewriter(aiMessage);
      }

      // 更新时间戳
      if (data.created_at) {
        aiMessage.time = new Date(data.created_at * 1000).toISOString();
      }
    },

    /**
     * 处理工作流结束事件
     * @param {object} data - 事件数据
     */
    handleWorkflowFinished(data) {
      // 重置当前答案
      this.currentAnswer = "";

      // 查找并更新对应消息的流式传输状态
      const aiMessage = this.findAIMessage(this.currentMessageId);
      if (aiMessage) {
        aiMessage.isStreaming = false;
        const answer = data?.data?.output?.answer;
        if (answer) {
          aiMessage.content = answer;
          aiMessage.displayedContent = answer;
          this.typewriterQueue = [];
          this.$forceUpdate();
        }
      }
      // 停止打字机效果
      if (this.stopTypewriter) {
        this.stopTypewriter();
      }

      // 从 AI 回答中解析相关问题
      const relatedQuestions = aiMessage ? parseRelatedQuestions(aiMessage.content) : [];

      // 移除显示内容中的相关问题部分
      if (aiMessage && aiMessage.content) {
        const cleanContent = removeRelatedQuestions(aiMessage.content);
        aiMessage.content = cleanContent;
        aiMessage.displayedContent = cleanContent;
        this.$forceUpdate();
      }

      const isNearBottom = this.isNearBottom ? this.isNearBottom() : false;

      // 更新相关问题数据
      if (this.updateRelatedQuestions) {
        this.updateRelatedQuestions(relatedQuestions);
      }
      if (isNearBottom) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }

      this.currentMessageId = null;
    },

    /**
     * 关闭 SSE 连接
     */
    closeSSEConnection() {
      if (this.sseCloseConnection) {
        this.sseCloseConnection();
        this.sseCloseConnection = null;
      }
    },
  },

  beforeDestroy() {
    // 组件销毁前关闭 SSE 连接
    this.closeSSEConnection();
  },
};
